import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/store';
import { updateConstant } from '@/store/slices/constantsSlice';
import type { ConstantMeta } from '@/services/adminApi';

type Section = ConstantMeta['section'];
const SECTION_ORDER: Section[] = ['Player', 'History', 'Lobby'];

interface RowProps {
  meta: ConstantMeta;
}

function ConstantRow({ meta }: RowProps): JSX.Element {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const currentValue = useAppSelector((s) => s.constants.values[meta.key] ?? meta.default);
  const savingState = useAppSelector((s) => s.constants.saving[meta.key] ?? 'idle');

  const [pending, setPending] = useState<number>(currentValue);

  // If another tab/saver changes the value, follow along when the user hasn't
  // touched the pending value (i.e. pending still matches the previously-known
  // current). This keeps stale "Save" buttons from re-applying old choices.
  // The dependency on currentValue/pending intentionally — re-running on every
  // render would be a bug.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useMemo(() => { if (pending !== currentValue) return; }, [currentValue]);

  const dirty = pending !== currentValue;
  const isSaving = savingState === 'saving';

  async function onSave(): Promise<void> {
    await dispatch(updateConstant({ key: meta.key, value: pending }));
  }

  const inputId = `constant-${meta.key}`;
  return (
    <div className="admin-constant-row">
      <div className="admin-constant-row__label">
        <label htmlFor={inputId}><strong>{meta.key}</strong></label>
        <p className="muted">{meta.description}</p>
      </div>
      <div className="admin-constant-row__control">
        <select
          id={inputId}
          value={pending}
          disabled={isSaving}
          onChange={(e): void => setPending(Number(e.target.value))}
        >
          {meta.options.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
        <button
          type="button"
          className="btn"
          onClick={(): void => void onSave()}
          disabled={!dirty || isSaving}
        >
          {isSaving ? t('common.saving') : t('common.save')}
        </button>
        {savingState === 'saved' && !dirty && (
          <span className="admin-constant-row__status" role="status">{t('admin.constants.saved')}</span>
        )}
        {savingState === 'error' && (
          <span className="admin-constant-row__status admin-constant-row__status--error" role="alert">
            {t('admin.constants.failed')}
          </span>
        )}
      </div>
    </div>
  );
}

export function ConstantsPage(): JSX.Element {
  const { t } = useTranslation();
  const status = useAppSelector((s) => s.constants.status);
  const registry = useAppSelector((s) => s.constants.registry);
  const error = useAppSelector((s) => s.constants.error);

  const grouped = useMemo(() => {
    const out: Record<Section, ConstantMeta[]> = { Player: [], History: [], Lobby: [] };
    for (const meta of registry) {
      out[meta.section].push(meta);
    }
    return out;
  }, [registry]);

  return (
    <main className="admin-page">
      <header className="admin-page__header">
        <h1>{t('admin.constants.title')}</h1>
        <p className="muted">
          {t('admin.constants.blurb')}
        </p>
      </header>

      {status === 'loading' && registry.length === 0 && (
        <p className="muted" role="status">{t('admin.constants.loading')}</p>
      )}
      {status === 'error' && registry.length === 0 && (
        <p className="error" role="alert">{error ?? t('admin.constants.loadError')}</p>
      )}

      {SECTION_ORDER.map((section) => {
        const items = grouped[section];
        if (!items || items.length === 0) return null;
        return (
          <section key={section} className="admin-card">
            <h2 className="admin-card__title">{t(`admin.constants.sections.${section}`)}</h2>
            <div className="admin-constants-list">
              {items.map((meta) => (
                <ConstantRow key={meta.key} meta={meta} />
              ))}
            </div>
          </section>
        );
      })}
    </main>
  );
}
